import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { LessonRecurringAvailabilityLabel } from './lesson-recurring'

import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import { LessonRecurringModal } from './enums'
import { useLessonRecurringStore } from './recurring/store'
import { useLessonRecurringSubmit } from './lesson-recurring-submit.hook'
import { formatTutorName } from './lessons'
import { getRecurringAvailabilityLabel, isTutorFullyAvailable } from './lesson-recurring'

interface LessonRecurringTutorReturn {
  refModal: ComputedRef<LessonRecurringModal | null>
  refTutorId: WritableComputedRef<number | undefined>
  refTutorOptions: ComputedRef<AppSelectOption<number>[]>
  refIsTutorsLoading: ComputedRef<boolean>
  refHasNoTutors: ComputedRef<boolean>
  refSelectedLabel: ComputedRef<LessonRecurringAvailabilityLabel | null>
  refTutorLabels: ComputedRef<Map<number, LessonRecurringAvailabilityLabel>>
  refIsSubmitting: ComputedRef<boolean>
  confirmTutor: () => Promise<void>
  confirmBusyTutor: () => Promise<void>
  backToTutor: () => void
  close: () => void
}

/** The tutor modal of the recurring form: a tutor with their availability for the schedule, and the warning when
 *  they cannot take every lesson, as the legacy tutor and availability-confirm modals did. */
export const useLessonRecurringTutor = (): LessonRecurringTutorReturn => {
  const toast = useToast()
  const store = useLessonRecurringStore()
  const listsStore = useListsStore()
  const { submitRecurring } = useLessonRecurringSubmit()

  const refModal = computed<LessonRecurringModal | null>(() => store.getModal)

  const refTutorId = computed<number | undefined>({
    get: () => store.getTutorId ?? undefined,
    set: (value) => {
      store.setTutorId(value ?? null)
    },
  })

  const refTutorOptions = computed<AppSelectOption<number>[]>(() => {
    const paidLabels = listsStore.getLists?.tutor.paid_label ?? {}

    return store.getTutors.map(tutor => ({
      value: tutor.id,
      text: formatTutorName(tutor.full_name, tutor.paid_label, paidLabels),
    }))
  })

  /** The colored availability tag of every listed tutor, shown beside their name as the legacy list did. */
  const refTutorLabels = computed<Map<number, LessonRecurringAvailabilityLabel>>(() =>
    new Map(store.getTutors.map(tutor => [tutor.id, getRecurringAvailabilityLabel(tutor)])))

  const refIsTutorsLoading = computed<boolean>(() => store.isTutorsLoading)

  /** The read is back and nobody matches the schedule and filters: the modal says so instead of an empty select. */
  const refHasNoTutors = computed<boolean>(() => !store.isTutorsLoading && store.getTutors.length === 0)

  const refSelectedLabel = computed<LessonRecurringAvailabilityLabel | null>(() => {
    const tutor = store.getSelectedTutor
    return tutor ? getRecurringAvailabilityLabel(tutor) : null
  })

  const refIsSubmitting = computed<boolean>(() => store.isSubmitting)

  const confirmTutor = async (): Promise<void> => {
    const tutor = store.getSelectedTutor

    if (!tutor) {
      toast.warning('Select a tutor to confirm the schedule.')
      return
    }

    if (!isTutorFullyAvailable(tutor)) {
      store.setModal(LessonRecurringModal.Busy)
      return
    }

    await submitRecurring(tutor.id, tutor.availability)
  }

  const confirmBusyTutor = async (): Promise<void> => {
    const tutor = store.getSelectedTutor
    if (!tutor) return

    await submitRecurring(tutor.id, tutor.availability)
  }

  const backToTutor = (): void => {
    store.setModal(LessonRecurringModal.Tutor)
  }

  const close = (): void => {
    if (store.isSubmitting) return
    store.setModal(null)
  }

  return {
    refModal,
    refTutorId,
    refTutorOptions,
    refIsTutorsLoading,
    refHasNoTutors,
    refSelectedLabel,
    refTutorLabels,
    refIsSubmitting,
    confirmTutor,
    confirmBusyTutor,
    backToTutor,
    close,
  }
}
